import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Add from '@mui/icons-material/Add';
import AddCircle from '@mui/icons-material/AddCircle';
import CloseIcon from '@mui/icons-material/Close';
import CloudUpload from '@mui/icons-material/CloudUpload';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import Grid from '@mui/material/Grid2';
import { PageLoader } from '../../shared/PageLoader';
import { EmptyStatePlaceholder } from '@/components/Analysis/shared/EmptyStatePlaceholder';
import { DropDownField } from '@/components/shared/CustomField';
import { TemplateFields } from '../TemplateForm/shared';
import { BulkOptionsModal } from '../TemplateForm/BulkOptionsModal';
import { GenericDialog } from '../../shared/GenericDialog';
import {
  DropDownContainer,
  dropDownStyles,
  Field,
  FieldPanel,
  FieldsList,
  FormHeader,
  LineBreak,
  OptionBox,
  TemplateFieldContainer,
  tooltipStyles,
} from '../TemplateForm/styles';
import { containedButtonStyle } from '@/theme';
import { abyssColors } from '@/theme/colors';
import { qaqcTemplateFields, selectedQaqcTemplateField, selectedQaqcTemplateId } from '../state';
import { formatEnumForDisplay, upperSnakeCase } from '@/utils';
import {
  buildUniqueFieldKey,
  cloneFields,
  filterIncomingOptions,
  isNewQaqcField,
  removeTypename,
  useSnackBarMessage,
} from '../utils';
import { generateMongoObjectId } from '@/utils/databaseHelpers';
import {
  AllLogsTemplatesByTypeDocument,
  GetVersionsDocument,
  LogTemplateType,
  LogsTemplateDocument,
  LogsTemplateField,
  SectionUnitsEnum,
  SortTypeEnum,
  TableCellStyleEnum,
  TemplateFieldTypeEnum,
  TextFormatEnum,
  useUpdateLogsTemplateMutation,
} from '@/__generated__/graphql';
import { NEW_FIELD_PREFIX } from '../constants';

const defaultQaqcDisplayConfig = () => ({
  id: generateMongoObjectId(),
  type: TableCellStyleEnum.Text,
  color: [] as { key: string; value: string }[],
  isSortable: false,
  sortType: SortTypeEnum.String,
  inputAdorment: null,
  textAreaRows: 2,
  textFormat: TextFormatEnum.None,
});

type QaqcFieldsPanelProps = {
  structureId: string;
  emptyForm: boolean;
  handleEmptyForm: (value: boolean) => void;
};

export const QaqcFieldsPanel = ({
  structureId,
  emptyForm,
  handleEmptyForm,
}: QaqcFieldsPanelProps) => {
  const [mode, setMode] = useState<'ReadOnly' | 'Edit'>('ReadOnly');
  const qaqcTemplateId = useAtomValue(selectedQaqcTemplateId);
  const [allFields, setAllFields] = useAtom(qaqcTemplateFields);
  const [selectedField, setSelectedField] = useAtom(selectedQaqcTemplateField);
  const [newOption, setNewOption] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const userSelectedFieldRef = useRef<LogsTemplateField | undefined>(undefined);
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [showDeleteFieldDialog, setShowDeleteFieldDialog] = useState(false);
  const editSnapshotRef = useRef<LogsTemplateField[] | null>(null);
  const baselineOptionsByFieldId = useRef<Map<string, Set<string>>>(new Map());

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  const beginEditMode = useCallback(() => {
    editSnapshotRef.current = cloneFields(allFields);
    baselineOptionsByFieldId.current.clear();
    for (const field of allFields ?? []) {
      baselineOptionsByFieldId.current.set(field.id, new Set(field.options ?? []));
    }
    setMode('Edit');
  }, [allFields]);

  const cancelEdit = useCallback(() => {
    if (editSnapshotRef.current !== null) {
      const restored = cloneFields(editSnapshotRef.current);
      setAllFields(restored.length > 0 ? restored : undefined);
      const first = restored[0];
      if (first) {
        setSelectedField(first.displayConfig ? first : { ...first, displayConfig: undefined });
        userSelectedFieldRef.current = first;
      } else {
        setSelectedField(undefined);
        userSelectedFieldRef.current = undefined;
      }
    }
    editSnapshotRef.current = null;
    baselineOptionsByFieldId.current.clear();
    setMode('ReadOnly');
    handleEmptyForm(false);
  }, [handleEmptyForm, setAllFields, setSelectedField]);

  const isOptionBaseline = useCallback((fieldId: string, option: string) => {
    return baselineOptionsByFieldId.current.get(fieldId)?.has(option) ?? false;
  }, []);

  const filteredFields = useMemo(() => {
    if (!allFields) {
      return [];
    }
    const query = searchTerm.trim().toLowerCase();
    return allFields.filter((field) =>
      query ? (field.name ?? '').toLowerCase().includes(query) : true
    );
  }, [allFields, searchTerm]);

  useEffect(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) {
      if (
        userSelectedFieldRef.current &&
        allFields?.some((f) => f.id === userSelectedFieldRef.current?.id)
      ) {
        return;
      }
      return;
    }
    if (filteredFields.length > 0) {
      const currentId = selectedField?.id;
      if (filteredFields.some((f) => f.id === currentId)) {
        return;
      }
      const first = filteredFields[0];
      setSelectedField(first.displayConfig ? first : { ...first, displayConfig: undefined });
      userSelectedFieldRef.current = first;
    }
  }, [searchTerm, filteredFields, allFields, selectedField?.id, setSelectedField]);

  const updateField = useCallback(
    (field: LogsTemplateField | undefined) => {
      if (!field) {
        return;
      }
      let next = field;
      if (isNewQaqcField(field)) {
        const key = buildUniqueFieldKey(field.name ?? '', allFields ?? [], field.id);
        if (key !== field.key) {
          next = { ...field, key };
        }
      }
      setSelectedField(next);
      userSelectedFieldRef.current = next;
      setAllFields((previous) => {
        if (!previous) {
          return undefined;
        }
        return previous.map((item) => (item.id === next.id ? next : item));
      });
    },
    [allFields, setAllFields, setSelectedField]
  );

  const handleDeleteField = useCallback(() => {
    if (!selectedField) {
      return;
    }
    const deletedId = selectedField.id;
    setAllFields((previous) => {
      if (!previous) {
        return undefined;
      }
      const next = previous.filter((f) => f.id !== deletedId);
      if (next.length > 0) {
        const nextField = next[0];
        setTimeout(() => {
          setSelectedField(
            nextField.displayConfig ? nextField : { ...nextField, displayConfig: undefined }
          );
          userSelectedFieldRef.current = nextField;
        }, 0);
      } else {
        setSelectedField(undefined);
        userSelectedFieldRef.current = undefined;
      }
      return next;
    });
    setShowDeleteFieldDialog(false);
  }, [selectedField, setAllFields, setSelectedField]);

  const [updateTemplate] = useUpdateLogsTemplateMutation({
    refetchQueries: [
      AllLogsTemplatesByTypeDocument,
      LogsTemplateDocument,
      {
        query: AllLogsTemplatesByTypeDocument,
        variables: {
          input: { structureId, type: LogTemplateType.QaqcTemplate },
        },
      },
    ],
    onCompleted: (response) => {
      if (!response?.updateLogsTemplate) {
        showSnackBar('An error occurred while updating QA/QC template', 'error');
        return;
      }
      showSnackBar('QA/QC template saved successfully', 'success');
      editSnapshotRef.current = null;
      baselineOptionsByFieldId.current.clear();
      setMode('ReadOnly');
    },
    onError: (error) => {
      console.error(error);
      showSnackBar(error.message, 'error');
    },
  });

  const cleanField = (field: LogsTemplateField) => {
    const cleaned = removeTypename(field) as LogsTemplateField;
    for (const key of Object.keys(cleaned)) {
      if (cleaned[key as keyof LogsTemplateField] === null) {
        delete cleaned[key as keyof LogsTemplateField];
      }
    }
    if (isNewQaqcField(field)) {
      const fieldWithoutId = cleaned as LogsTemplateField & { id?: string };
      const { id: _unusedNewFieldId, ...rest } = fieldWithoutId;
      void _unusedNewFieldId;
      return { ...rest, id: generateMongoObjectId() } as LogsTemplateField;
    }
    return cleaned;
  };

  const handleSaveTemplate = useCallback(() => {
    if (!qaqcTemplateId || !allFields) {
      showSnackBar('Missing template', 'error');
      return;
    }

    const duplicateNew: LogsTemplateField[] = [];
    const existingKeys = new Set<string>();
    const existingNames = new Set<string>();

    for (const field of allFields) {
      if (!isNewQaqcField(field)) {
        if (field.key) {
          existingKeys.add(field.key);
        }
        existingNames.add(field.name?.trim() ?? '');
      }
    }

    for (const field of allFields) {
      if (!isNewQaqcField(field)) {
        continue;
      }
      const key = field.key ?? '';
      const name = field.name?.trim() ?? '';
      if (existingKeys.has(key) || existingNames.has(name)) {
        duplicateNew.push(field);
      } else {
        existingKeys.add(key);
        existingNames.add(name);
      }
    }

    if (duplicateNew.length > 0) {
      showSnackBar('Remove duplicate new field names or keys before saving.', 'error');
      return;
    }

    for (const field of allFields) {
      if (isNewQaqcField(field) && !(field.name ?? '').trim()) {
        showSnackBar('Each new field must have a name.', 'error');
        return;
      }
    }

    updateTemplate({
      variables: {
        input: {
          structureId,
          templateId: qaqcTemplateId,
          fields: allFields.map(cleanField),
          lastModifiedFor: 'Update QA/QC Template Fields',
        },
      },
      refetchQueries: [GetVersionsDocument],
    });
  }, [allFields, qaqcTemplateId, showSnackBar, structureId, updateTemplate]);

  const handleRemoveOption = (option: string) => {
    if (!selectedField) {
      return;
    }
    if (isOptionBaseline(selectedField.id, option)) {
      return;
    }
    updateField({
      ...selectedField,
      options: selectedField.options?.filter((item) => item !== option) ?? [],
    });
  };

  const tryAddSingleOption = () => {
    if (!selectedField || !newOption.trim()) {
      return;
    }
    const existing = selectedField.options ?? [];
    const { accepted, skipped } = filterIncomingOptions(existing, [newOption]);
    if (skipped > 0) {
      showSnackBar('That option already exists (case-insensitive match).', 'error');
      return;
    }
    if (accepted.length > 0) {
      updateField({
        ...selectedField,
        options: [...existing, ...accepted],
      });
    }
    setNewOption('');
  };

  const handleBulkOptions = (incoming: string[]) => {
    if (!selectedField) {
      return;
    }
    const existing = selectedField.options ?? [];
    const { accepted, skipped } = filterIncomingOptions(existing, incoming);
    if (accepted.length > 0) {
      updateField({
        ...selectedField,
        options: [...existing, ...accepted],
      });
    }
    if (skipped > 0) {
      showSnackBar(`${skipped} option(s) skipped (already exist or duplicate in file).`, 'warning');
    }
  };

  const hasSelectOptions = (type: TemplateFieldTypeEnum) =>
    type === TemplateFieldTypeEnum.SingleSelect ||
    type === TemplateFieldTypeEnum.MultiSelect ||
    type === TemplateFieldTypeEnum.RadioButton;

  const hasFields = allFields && allFields.length > 0;
  if (hasFields && !selectedField) {
    return <PageLoader />;
  }

  const showOptionsSection = selectedField && hasSelectOptions(selectedField.type);

  return (
    <>
      {showOptionsModal && (
        <BulkOptionsModal
          open={showOptionsModal}
          enableColors={false}
          handleUpdate={handleBulkOptions}
          handleClose={() => setShowOptionsModal(false)}
        />
      )}
      {showDeleteFieldDialog && (
        <GenericDialog
          open={showDeleteFieldDialog}
          onClose={() => setShowDeleteFieldDialog(false)}
          title="Delete Field"
          content={
            <Typography fontWeight={400} fontSize="14px" color={abyssColors.primary[500]}>
              Are you sure you want to delete this field?
            </Typography>
          }
          actions={
            <>
              <Button
                variant="text"
                onClick={() => setShowDeleteFieldDialog(false)}
                sx={{ color: abyssColors.primary[500] }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => handleDeleteField()}
                sx={containedButtonStyle}
              >
                Delete
              </Button>
            </>
          }
          actionsStyles={{ mr: 3, pb: 3 }}
        />
      )}

      {hasFields && (
        <Box sx={{ pt: 1, pb: 2 }}>
          <TextField
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search fields"
            variant="outlined"
            size="small"
            fullWidth
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: !!searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="Clear search"
                      onClick={() => setSearchTerm('')}
                      edge="end"
                      size="small"
                    >
                      <CloseIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>
      )}

      {(() => {
        if (filteredFields.length > 0 && selectedField) {
          return (
            <Stack>
              <FormHeader>
                {emptyForm ? (
                  <TemplateFields
                    value=""
                    placeholder="Template Name"
                    mode={emptyForm ? 'Edit' : mode}
                    onChange={(value) => {
                      updateField({ ...selectedField, name: value });
                    }}
                  />
                ) : (
                  <Typography fontSize="2.2rem" fontWeight={500}>
                    {selectedField.name || 'Untitled field'}
                  </Typography>
                )}

                {mode === 'Edit' || emptyForm ? (
                  <Stack direction="row" spacing={1} sx={{ pb: 0.5 }}>
                    <Button
                      variant="contained"
                      sx={{ ...containedButtonStyle, fontSize: '12px', py: 1 }}
                      onClick={() => {
                        if (emptyForm) {
                          handleEmptyForm(false);
                        } else {
                          handleSaveTemplate();
                        }
                      }}
                    >
                      Save
                    </Button>
                    <Button
                      variant="contained"
                      sx={{ ...containedButtonStyle, fontSize: '12px', py: 1 }}
                      onClick={() => {
                        if (emptyForm) {
                          handleEmptyForm(false);
                        } else {
                          cancelEdit();
                        }
                      }}
                    >
                      Cancel
                    </Button>
                  </Stack>
                ) : (
                  <Stack direction="row" spacing={0} sx={{ pb: 0.5 }}>
                    <Tooltip
                      title="Edit Template"
                      placement="top"
                      arrow
                      slotProps={{
                        popper: {
                          sx: { tooltipStyles },
                        },
                      }}
                    >
                      <IconButton size="medium" onClick={() => beginEditMode()}>
                        <Edit
                          fontSize="inherit"
                          sx={{ cursor: 'pointer', color: abyssColors.primary[500] }}
                        />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                )}
              </FormHeader>

              <LineBreak marginBottom={2} />
              <Stack direction="row" width="100%" gap={2}>
                <FieldsList>
                  <Stack gap={1}>
                    {filteredFields.map((field) => (
                      <Field
                        selected={selectedField?.id === field.id}
                        key={field.id}
                        onClick={() => {
                          const next = field.displayConfig
                            ? field
                            : { ...field, displayConfig: undefined };
                          setSelectedField(next);
                          userSelectedFieldRef.current = next;
                        }}
                      >
                        {field.name}
                      </Field>
                    ))}
                  </Stack>
                  {(mode === 'Edit' || emptyForm) && (
                    <Button
                      sx={{ marginTop: 2, height: '40px' }}
                      startIcon={<Add />}
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        const id = `${NEW_FIELD_PREFIX}${generateMongoObjectId()}`;
                        const newField = {
                          id,
                          name: '',
                          options: [],
                          type: TemplateFieldTypeEnum.SingleSelect,
                          isRequired: false,
                          isReadOnly: false,
                          serverSide: false,
                          key: buildUniqueFieldKey('field', allFields ?? [], id),
                          displayConfig: defaultQaqcDisplayConfig(),
                          fieldUnit: SectionUnitsEnum.Na,
                          roundToDecimal: null,
                          isQaqc: true,
                          allowsMultipleValues: false,
                          isHidden: false,
                          defaultVisible: true,
                          displayInLogging: true,
                          displayInQuery: false,
                          displayInTable: false,
                          displayInReview: false,
                          displayInReports: false,
                          displayInReportFilters: false,
                          displayInLogAggregation: false,
                          isDerived: false,
                        };
                        baselineOptionsByFieldId.current.set(newField.id, new Set());
                        setAllFields([...(allFields ?? []), newField]);
                        setSelectedField(newField);
                        userSelectedFieldRef.current = newField;
                      }}
                    >
                      Add Field
                    </Button>
                  )}
                </FieldsList>

                <FieldPanel gap={3} direction="row">
                  <Stack width="50%" gap={1}>
                    {(mode === 'Edit' || emptyForm) && (
                      <TemplateFields
                        label="Name"
                        value={selectedField.name}
                        mode={emptyForm ? 'Edit' : mode}
                        onChange={(value) => {
                          updateField({ ...selectedField, name: value });
                        }}
                      />
                    )}

                    <TemplateFields
                      label="Key"
                      value={selectedField.key}
                      mode="ReadOnly"
                      onChange={() => {}}
                    />
                    {isNewQaqcField(selectedField) && (mode === 'Edit' || emptyForm) && (
                      <Typography variant="caption" color="text.secondary">
                        Key is auto-generated from the field name and must stay unique.
                      </Typography>
                    )}

                    <DropDownContainer>
                      <Typography>Field Type</Typography>
                      <DropDownField
                        mode={emptyForm ? 'Edit' : mode}
                        id={selectedField.id}
                        title=""
                        sx={dropDownStyles}
                        disableClearable
                        value={formatEnumForDisplay(selectedField.type) || ''}
                        options={Object.values(TemplateFieldTypeEnum).map((value) =>
                          formatEnumForDisplay(value)
                        )}
                        onFieldValueChange={(value) => {
                          if (!value || !selectedField) {
                            return;
                          }
                          const type =
                            Object.values(TemplateFieldTypeEnum).find(
                              (enumValue) => enumValue === upperSnakeCase(value)
                            ) ?? TemplateFieldTypeEnum.Input;
                          updateField({
                            ...selectedField,
                            type,
                            options: hasSelectOptions(type) ? (selectedField.options ?? []) : [],
                          });
                        }}
                      />
                    </DropDownContainer>

                    <Grid container maxWidth="60rem" ml="-1.5rem">
                      <Grid size={12}>
                        <Stack direction="row" alignItems="center">
                          <Checkbox
                            checked={selectedField.isQaqc ?? false}
                            disabled={mode === 'ReadOnly' && !emptyForm}
                            onChange={(event) => {
                              updateField({
                                ...selectedField,
                                isQaqc: event.target.checked,
                              });
                            }}
                          />
                          <Typography>QA/QC field (isQaqc)</Typography>
                        </Stack>
                      </Grid>
                    </Grid>

                    <Grid container maxWidth="60rem" ml="-1.5rem">
                      <Grid size={12}>
                        <Stack direction="row" alignItems="center">
                          <Checkbox
                            checked={selectedField.isDerived ?? false}
                            disabled={mode === 'ReadOnly' && !emptyForm}
                            onChange={(event) => {
                              updateField({
                                ...selectedField,
                                isDerived: event.target.checked,
                              });
                            }}
                          />
                          <Typography>Is Derived?</Typography>
                        </Stack>
                      </Grid>
                    </Grid>

                    <Grid container maxWidth="60rem" ml="-1.5rem">
                      <Grid size={12}>
                        <Stack direction="row" alignItems="center">
                          <Checkbox
                            checked={(selectedField as any).allowsMultipleValues ?? false}
                            disabled={mode === 'ReadOnly' && !emptyForm}
                            onChange={(event) => {
                              const isNumericField =
                                selectedField.type === TemplateFieldTypeEnum.Numeric;
                              const allowsMultipleValues = event.target.checked;

                              updateField({
                                ...selectedField,
                                allowsMultipleValues: isNumericField ? allowsMultipleValues : false,
                              } as any);
                            }}
                          />
                          <Typography>Allows multiple values</Typography>
                        </Stack>
                      </Grid>
                    </Grid>

                    <DropDownContainer>
                      <Typography>Field unit</Typography>
                      <DropDownField
                        mode={emptyForm ? 'Edit' : mode}
                        id={`${selectedField.id}-unit`}
                        title=""
                        sx={dropDownStyles}
                        value={formatEnumForDisplay(selectedField.fieldUnit) || '-'}
                        options={Object.values(SectionUnitsEnum).map((value) =>
                          formatEnumForDisplay(value)
                        )}
                        onFieldValueChange={(value) => {
                          const fieldUnit = value
                            ? Object.values(SectionUnitsEnum).find(
                                (enumValue) => enumValue === upperSnakeCase(value)
                              )
                            : null;
                          updateField({
                            ...selectedField,
                            fieldUnit: fieldUnit ?? null,
                          });
                        }}
                      />
                    </DropDownContainer>

                    <TemplateFields
                      label="Round to decimal"
                      value={selectedField.roundToDecimal?.toString() ?? ''}
                      onChange={(value) => {
                        const number_ = Number.parseInt(value, 10);
                        updateField({
                          ...selectedField,
                          roundToDecimal: Number.isNaN(number_) ? null : number_,
                        });
                      }}
                      mode={emptyForm ? 'Edit' : mode}
                      type={TemplateFieldTypeEnum.Numeric}
                    />

                    {selectedField.displayConfig?.isSortable && (
                      <Stack
                        direction="row"
                        gap={1}
                        alignItems="center"
                        width="100%"
                        maxWidth="50rem"
                        justifyContent="space-between"
                      >
                        <Typography>Sort type</Typography>
                        <DropDownField
                          mode={emptyForm ? 'Edit' : mode}
                          id={`${selectedField.id}-sort`}
                          title=""
                          sx={dropDownStyles}
                          disableClearable
                          value={formatEnumForDisplay(selectedField.displayConfig.sortType) || ''}
                          options={Object.values(SortTypeEnum).map((value) =>
                            formatEnumForDisplay(value)
                          )}
                          onFieldValueChange={(value) => {
                            if (!value) {
                              return;
                            }
                            const sortType =
                              Object.values(SortTypeEnum).find(
                                (enumValue) => enumValue === upperSnakeCase(value)
                              ) ?? SortTypeEnum.String;
                            updateField({
                              ...selectedField,
                              displayConfig: {
                                ...selectedField.displayConfig!,
                                sortType,
                              },
                            });
                          }}
                        />
                      </Stack>
                    )}

                    <Stack
                      direction="row"
                      gap={1}
                      alignItems="center"
                      width="100%"
                      maxWidth="50rem"
                      justifyContent="space-between"
                    >
                      <Typography>Text format</Typography>
                      <DropDownField
                        mode={emptyForm ? 'Edit' : mode}
                        id={`${selectedField.id}-textformat`}
                        title=""
                        sx={dropDownStyles}
                        disableClearable
                        value={formatEnumForDisplay(selectedField.displayConfig?.textFormat) || ''}
                        options={Object.values(TextFormatEnum).map((value) =>
                          formatEnumForDisplay(value)
                        )}
                        onFieldValueChange={(value) => {
                          if (!value) {
                            return;
                          }
                          const textFormat =
                            Object.values(TextFormatEnum).find(
                              (enumValue) => enumValue === upperSnakeCase(value)
                            ) ?? TextFormatEnum.None;
                          const base = selectedField.displayConfig ?? defaultQaqcDisplayConfig();
                          updateField({
                            ...selectedField,
                            displayConfig: { ...base, textFormat },
                          });
                        }}
                      />
                    </Stack>

                    {selectedField.type === TemplateFieldTypeEnum.Textarea && (
                      <TemplateFields
                        label="Text area rows"
                        value={selectedField.displayConfig?.textAreaRows?.toString() ?? ''}
                        mode={emptyForm ? 'Edit' : mode}
                        type={TemplateFieldTypeEnum.Numeric}
                        onChange={(value) => {
                          const rows = Number.parseInt(value, 10);
                          const base = selectedField.displayConfig ?? defaultQaqcDisplayConfig();
                          updateField({
                            ...selectedField,
                            displayConfig: {
                              ...base,
                              textAreaRows: Number.isNaN(rows) ? undefined : rows,
                            },
                          });
                        }}
                      />
                    )}

                    <DropDownContainer>
                      <Typography>Display type</Typography>
                      <DropDownField
                        mode={emptyForm ? 'Edit' : mode}
                        id={`${selectedField.id}-display`}
                        title=""
                        sx={dropDownStyles}
                        disableClearable
                        value={formatEnumForDisplay(selectedField.displayConfig?.type) || ''}
                        options={Object.values(TableCellStyleEnum).map((value) =>
                          formatEnumForDisplay(value)
                        )}
                        onFieldValueChange={(value) => {
                          if (!value) {
                            return;
                          }
                          const displayType =
                            Object.values(TableCellStyleEnum).find(
                              (enumValue) => enumValue === upperSnakeCase(value)
                            ) ?? TableCellStyleEnum.Text;
                          const base = selectedField.displayConfig ?? defaultQaqcDisplayConfig();
                          updateField({
                            ...selectedField,
                            displayConfig: { ...base, type: displayType },
                          });
                        }}
                      />
                    </DropDownContainer>

                    <Stack direction="row" alignItems="center" gap={1} maxWidth="50rem">
                      <Checkbox
                        checked={selectedField.displayConfig?.isSortable ?? false}
                        disabled={mode === 'ReadOnly' && !emptyForm}
                        onChange={(event) => {
                          const base = selectedField.displayConfig ?? defaultQaqcDisplayConfig();
                          updateField({
                            ...selectedField,
                            displayConfig: {
                              ...base,
                              isSortable: event.target.checked,
                              sortType: base.sortType ?? SortTypeEnum.String,
                            },
                          });
                        }}
                      />
                      <Typography>Sortable</Typography>
                    </Stack>

                    {(mode === 'Edit' || emptyForm) && isNewQaqcField(selectedField) && (
                      <Button
                        sx={{ width: '50%', height: '40px' }}
                        startIcon={<Delete />}
                        variant="outlined"
                        size="small"
                        onClick={() => setShowDeleteFieldDialog(true)}
                      >
                        Remove field
                      </Button>
                    )}
                  </Stack>

                  {showOptionsSection && (
                    <Stack sx={{ width: '50%' }} gap={2}>
                      <Stack direction="row" alignItems="center" gap={1}>
                        <Typography fontWeight={500} fontSize="1.6rem">
                          Options
                        </Typography>
                        <Typography variant="subtitle2">
                          ({selectedField.options?.length ?? 0})
                        </Typography>
                      </Stack>

                      {!isNewQaqcField(selectedField) && (mode === 'Edit' || emptyForm) && (
                        <Alert severity="info" sx={{ py: 0.5 }}>
                          Existing options cannot be edited or removed. You can add new options
                          below.
                        </Alert>
                      )}

                      <TemplateFieldContainer>
                        {selectedField.options?.map((option, index) => (
                          <OptionBox key={`${option}-${index}`}>
                            <Typography>{option}</Typography>
                            {(mode === 'Edit' || emptyForm) &&
                              !isOptionBaseline(selectedField.id, option) && (
                                <Delete
                                  onClick={() => {
                                    handleRemoveOption(option);
                                  }}
                                />
                              )}
                          </OptionBox>
                        ))}
                      </TemplateFieldContainer>

                      {(mode === 'Edit' || emptyForm) && (
                        <>
                          <Stack
                            direction="row"
                            gap={2}
                            marginBottom={2}
                            width="100%"
                            alignItems="center"
                          >
                            <Stack width="100%">
                              <TemplateFields
                                label="New option"
                                value={newOption}
                                onChange={(value) => setNewOption(value ?? '')}
                                mode={emptyForm ? 'Edit' : mode}
                              />
                            </Stack>
                            <Button
                              sx={{ width: '20rem', height: '32px' }}
                              startIcon={<AddCircle />}
                              variant="outlined"
                              size="small"
                              disabled={!newOption.trim()}
                              onClick={() => tryAddSingleOption()}
                            >
                              Add option
                            </Button>
                          </Stack>
                          <Button
                            sx={{ width: '50%', height: '40px', ml: 'auto' }}
                            startIcon={<CloudUpload />}
                            variant="outlined"
                            size="small"
                            onClick={() => setShowOptionsModal(true)}
                          >
                            Add options from file
                          </Button>
                        </>
                      )}
                    </Stack>
                  )}
                </FieldPanel>
              </Stack>
            </Stack>
          );
        }

        if (!hasFields) {
          return (
            <Box sx={{ pt: 2 }}>
              <FieldPanel justifyContent="center" alignItems="center" sx={{ width: '100%' }}>
                <Stack spacing={3} alignItems="center">
                  <EmptyStatePlaceholder type="template" message="No QA/QC fields found" />
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    Get started by creating your first field
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => {
                      const id = `${NEW_FIELD_PREFIX}${generateMongoObjectId()}`;
                      const newField = {
                        id,
                        name: '',
                        options: [],
                        type: TemplateFieldTypeEnum.SingleSelect,
                        isRequired: false,
                        isReadOnly: false,
                        serverSide: false,
                        key: buildUniqueFieldKey('field', [], id),
                        displayConfig: defaultQaqcDisplayConfig(),
                        fieldUnit: SectionUnitsEnum.Na,
                        roundToDecimal: null,
                        isQaqc: true,
                        allowsMultipleValues: false,
                        isHidden: false,
                        defaultVisible: true,
                        displayInLogging: true,
                        displayInQuery: false,
                        displayInTable: false,
                        displayInReview: false,
                        displayInReports: false,
                        displayInReportFilters: false,
                        displayInLogAggregation: false,
                        isDerived: false,
                      };
                      editSnapshotRef.current = [];
                      baselineOptionsByFieldId.current.clear();
                      baselineOptionsByFieldId.current.set(newField.id, new Set());
                      setAllFields([newField]);
                      setSelectedField(newField);
                      userSelectedFieldRef.current = newField;
                      setMode('Edit');
                    }}
                    sx={containedButtonStyle}
                  >
                    Add first field
                  </Button>
                </Stack>
              </FieldPanel>
            </Box>
          );
        }

        return (
          <Box sx={{ pt: 2 }}>
            <FieldPanel justifyContent="center" alignItems="center" sx={{ width: '100%' }}>
              <EmptyStatePlaceholder type="template" message="No results found" />
            </FieldPanel>
          </Box>
        );
      })()}
    </>
  );
};
